var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useOkapiKy } from '@folio/stripes/core';
import { useMutation } from 'react-query';
import { Modal, ModalFooter, Button, TextField, Select, MessageBanner, IconButton, Row, Col, } from '@folio/stripes/components';
import { VOCABULARY_TYPE_CONFIG, SUPPORTED_LANGUAGES, } from '../types/api';
import { createVocabulary, fetchSimilarSpellings, fetchVocabulary, updateVocabulary, } from '../api/vocabularyService';
import { getErrorInfo } from '../utils/getErrorCode';
import { isTimeoutError } from '../utils/errorKind';
import { saveOutcomeUnknownError, verificationFromVersions, } from '../utils/saveOutcomeUnknown';
import SaveOutcomeUnknownNotice from './ProposalForm/SaveOutcomeUnknownNotice';
import { useVocabularyTypeOptions } from '../hooks/useVocabularyTypeOptions';
import { vocabularyConflictMessageId } from '../utils/vocabularyConflictMessageId';
var PRIMARY_LANGUAGE = 'cs';
var warningKey = function (type, heading) { return "".concat(type, "\0").concat(heading); };
/**
 * Builds the initial editor state from a vocabulary record. The primary (cs) row is always
 * present as the first row — populated from translations.cs when available, else from the
 * legacy flat name/code (backward compatibility during the rollout window).
 *
 * In create mode (no `vocabulary`), an optional `defaultName` seeds the primary row's name
 * (TASK-086) so a host can preload the form with text the user already typed.
 */
function buildInitialRows(vocabulary, defaultName) {
    var _a, _b, _c, _d;
    if (!vocabulary) {
        return [{ lang: PRIMARY_LANGUAGE, name: defaultName !== null && defaultName !== void 0 ? defaultName : '', code: '' }];
    }
    var translations = vocabulary.translations;
    var hasTranslations = translations && Object.keys(translations).length > 0;
    var csFromTranslations = hasTranslations ? translations[PRIMARY_LANGUAGE] : undefined;
    var primary = csFromTranslations
        ? {
            lang: PRIMARY_LANGUAGE,
            name: (_a = csFromTranslations.name) !== null && _a !== void 0 ? _a : '',
            code: (_b = csFromTranslations.code) !== null && _b !== void 0 ? _b : '',
        }
        : {
            lang: PRIMARY_LANGUAGE,
            name: (_c = vocabulary.name) !== null && _c !== void 0 ? _c : '',
            code: (_d = vocabulary.code) !== null && _d !== void 0 ? _d : '',
        };
    if (!hasTranslations) {
        return [primary];
    }
    var rest = SUPPORTED_LANGUAGES
        .filter(function (lang) { return lang !== PRIMARY_LANGUAGE && translations[lang]; })
        .map(function (lang) {
        var _a, _b, _c, _d;
        return ({
            lang: lang,
            name: (_b = (_a = translations[lang]) === null || _a === void 0 ? void 0 : _a.name) !== null && _b !== void 0 ? _b : '',
            code: (_d = (_c = translations[lang]) === null || _c === void 0 ? void 0 : _c.code) !== null && _d !== void 0 ? _d : '',
        });
    });
    return __spreadArray([primary], rest, true);
}
/**
 * Modal form for creating/editing vocabulary terms with multilingual translations (TASK-041).
 * The Czech (`cs`) row is always present and cannot be removed; English is optional and added
 * via the "+ Add translation" button. Only the primary row shows the `code` field.
 */
var VocabularyForm = function (_a) {
    var _b, _c, _d, _e, _f, _g;
    var vocabulary = _a.vocabulary, defaultType = _a.defaultType, defaultName = _a.defaultName, lockType = _a.lockType, onClose = _a.onClose, onSuccess = _a.onSuccess, onAdvancedEdit = _a.onAdvancedEdit;
    var intl = useIntl();
    var ky = useOkapiKy();
    var isEdit = Boolean(vocabulary);
    var _h = useState((vocabulary === null || vocabulary === void 0 ? void 0 : vocabulary.type) || defaultType || ''), type = _h[0], setType = _h[1];
    var _j = useState(function () { return buildInitialRows(vocabulary, defaultName); }), rows = _j[0], setRows = _j[1];
    /**
     * CZNA-1625 — a sentence, or (an edit the server did not answer in time) the structured
     * error whose banner offers "Načíst znovu".
     */
    var _k = useState(null), errorMessage = _k[0], setErrorMessage = _k[1];
    /** CZNA-1625 — a fresh banner per failure, so a previous check's outcome never lingers. */
    var _l = useState(0), failureSeq = _l[0], setFailureSeq = _l[1];
    var showError = useCallback(function (failure) {
        setErrorMessage(failure);
        if (failure !== null)
            setFailureSeq(function (n) { return n + 1; });
    }, []);
    /** CZNA-1625 — true while "Načíst znovu" reads the term; the footer is inert meanwhile. */
    var _m = useState(false), verifying = _m[0], setVerifying = _m[1];
    /**
     * CZNA-1625 — the version the next edit goes out on once a timed-out edit was verified as NOT
     * written (the version read). `from` pins it to the `vocabulary` prop it was read over; a
     * newer prop wins again.
     */
    var adoptedVersionRef = useRef(null);
    var _o = useState({}), fieldErrors = _o[0], setFieldErrors = _o[1];
    var _p = useState(null), similarWarning = _p[0], setSimilarWarning = _p[1];
    var _q = useState(false), checkingSimilar = _q[0], setCheckingSimilar = _q[1];
    // CZNA-1470 — the configured types as before, plus every OPEN registry type, so a
    // vocabulary created in Nastavení → Slovníky takes terms right away. A CLOSED registry
    // type stays out, as before: a cataloguer's create there is refused (CZNA-1310).
    var _r = useVocabularyTypeOptions(), allTypeOptions = _r.options, typeOf = _r.typeOf;
    var openTypeOptions = useMemo(function () { return allTypeOptions.filter(function (o) {
        var _a;
        return (Boolean(VOCABULARY_TYPE_CONFIG[o.value]) || ((_a = typeOf(o.value)) === null || _a === void 0 ? void 0 : _a.validationType) === 'OPEN');
    }); }, [allTypeOptions, typeOf]);
    var codeRequired = (_e = (_c = (_b = typeOf(type)) === null || _b === void 0 ? void 0 : _b.codeRequired) !== null && _c !== void 0 ? _c : (_d = VOCABULARY_TYPE_CONFIG[type]) === null || _d === void 0 ? void 0 : _d.codeRequired) !== null && _e !== void 0 ? _e : false;
    var availableLanguages = useMemo(function () { return SUPPORTED_LANGUAGES.filter(function (lang) { return !rows.some(function (r) { return r.lang === lang; }); }); }, [rows]);
    var canAddTranslation = availableLanguages.length > 0;
    var handleAddTranslation = useCallback(function () {
        if (!canAddTranslation)
            return;
        var nextLang = availableLanguages[0];
        setRows(function (prev) { return __spreadArray(__spreadArray([], prev, true), [{ lang: nextLang, name: '', code: '' }], false); });
    }, [availableLanguages, canAddTranslation]);
    var handleRemoveRow = useCallback(function (lang) {
        if (lang === PRIMARY_LANGUAGE)
            return;
        setRows(function (prev) { return prev.filter(function (r) { return r.lang !== lang; }); });
    }, []);
    var handleRowChange = useCallback(function (lang, patch) {
        setRows(function (prev) {
            return prev.map(function (r) { return (r.lang === lang ? __assign(__assign({}, r), patch) : r); });
        });
    }, []);
    var createMutation = useMutation(function (data) { return createVocabulary(ky, data); }, {
        onSuccess: function (created) { return onSuccess(created); },
        onError: function (err) { return __awaiter(void 0, void 0, void 0, function () {
            var errorInfo;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        // CZNA-1625 — the term may have been created: "zkuste znovu" would make a second one.
                        // No list link — the list (or the form the picker serves) sits right behind this modal.
                        if (isTimeoutError(err)) {
                            showError(intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.error.createTimeout' }));
                            return [2 /*return*/];
                        }
                        return [4 /*yield*/, getErrorInfo(err)];
                    case 1:
                        errorInfo = _a.sent();
                        showError(intl.formatMessage({
                            id: errorInfo.status === 409
                                ? vocabularyConflictMessageId(errorInfo, 'ui-czna-authorities.vocabularies.error.duplicate')
                                : 'ui-czna-authorities.error.generic',
                        }));
                        return [2 /*return*/];
                }
            });
        }); },
    });
    /**
     * CZNA-1625 — "Načíst znovu" under an edit the server did not answer in time: read the term
     * and compare its version with the one the edit went out on. Moved → it landed: what a
     * successful edit does (`onSuccess`). Unchanged → nothing was written: the edits stay, and the
     * next "Uložit" goes out on the version read. Unreadable → the banner stays and says so.
     */
    var verifyTimedOutUpdate = function (sentVersion) { return __awaiter(void 0, void 0, void 0, function () {
        var fresh, _a, outcome;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    if (!vocabulary)
                        return [2 /*return*/, 'unverified'];
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, , 4]);
                    return [4 /*yield*/, fetchVocabulary(ky, vocabulary.id)];
                case 2:
                    fresh = _b.sent();
                    return [3 /*break*/, 4];
                case 3:
                    _a = _b.sent();
                    return [2 /*return*/, 'unverified'];
                case 4:
                    outcome = verificationFromVersions(sentVersion, fresh === null || fresh === void 0 ? void 0 : fresh.version);
                    if (outcome === 'saved') {
                        onSuccess();
                    }
                    else if (outcome === 'notSaved') {
                        adoptedVersionRef.current = { from: vocabulary.version, version: fresh.version };
                    }
                    return [2 /*return*/, outcome];
            }
        });
    }); };
    var runVerify = function (verify) { return __awaiter(void 0, void 0, void 0, function () {
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    setVerifying(true);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, , 3, 4]);
                    return [4 /*yield*/, verify()];
                case 2: return [2 /*return*/, _a.sent()];
                case 3:
                    setVerifying(false);
                    return [7 /*endfinally*/];
                case 4: return [2 /*return*/];
            }
        });
    }); };
    var updateMutation = useMutation(function (data) { return updateVocabulary(ky, vocabulary.id, data); }, {
        onSuccess: function () { return onSuccess(); },
        onError: function (err, data) { return __awaiter(void 0, void 0, void 0, function () {
            var errorInfo;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        // CZNA-1625 — the edit may have landed; offer to find out instead of "zkuste znovu".
                        if (isTimeoutError(err)) {
                            showError(saveOutcomeUnknownError(intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.error.updateTimeout' }), { kind: 'verify', verify: function () { return verifyTimedOutUpdate(data === null || data === void 0 ? void 0 : data.version); } }));
                            return [2 /*return*/];
                        }
                        return [4 /*yield*/, getErrorInfo(err)];
                    case 1:
                        errorInfo = _a.sent();
                        showError(intl.formatMessage({
                            id: errorInfo.status === 409
                                ? vocabularyConflictMessageId(errorInfo, 'ui-czna-authorities.error.optimisticLockConflict')
                                : 'ui-czna-authorities.error.generic',
                        }));
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var isSubmitting = createMutation.isLoading || updateMutation.isLoading || checkingSimilar || verifying;
    var primaryHeading = ((_g = (_f = rows.find(function (r) { return r.lang === PRIMARY_LANGUAGE; })) === null || _f === void 0 ? void 0 : _f.name) !== null && _g !== void 0 ? _g : '').trim();
    var shownWarning = similarWarning && similarWarning.key === warningKey(type, primaryHeading)
        ? similarWarning.spellings
        : null;
    // What the dialog holds NOW, read after the check's await: the heading may have been edited,
    // or the dialog closed, while the check ran — and the create must not post what the user
    // has since changed or walked away from.
    var currentKeyRef = useRef(warningKey(type, primaryHeading));
    currentKeyRef.current = warningKey(type, primaryHeading);
    var mountedRef = useRef(true);
    useEffect(function () {
        mountedRef.current = true;
        return function () { mountedRef.current = false; };
    }, []);
    /**
     * CZNA-1346 — linking corpus values to a term is an exact match, so a term that differs
     * from a value only by a comma leaves that value unlinked. Asks the backend for such
     * near-duplicates and reports whether a warning is now on screen.
     *
     * A failed check lets the create through: the warning helps, it must never be the thing
     * that stops someone from adding a term (the ticket's own constraint). Nothing is lost —
     * the same request is what the user would have sent without this check.
     */
    var showsSimilarWarning = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var spellings, _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    setCheckingSimilar(true);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, fetchSimilarSpellings(ky, type, primaryHeading)];
                case 2:
                    spellings = _b.sent();
                    if (spellings.terms.length === 0 && spellings.dataValues.length === 0) {
                        return [2 /*return*/, false];
                    }
                    setSimilarWarning({ key: warningKey(type, primaryHeading), spellings: spellings });
                    return [2 /*return*/, true];
                case 3:
                    _a = _b.sent();
                    return [2 /*return*/, false];
                case 4:
                    setCheckingSimilar(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); }, [ky, type, primaryHeading]);
    var validate = useCallback(function () {
        var errors = {};
        if (!type) {
            errors.type = intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.form.typeRequired' });
        }
        rows.forEach(function (row) {
            if (!row.name.trim()) {
                errors["name:".concat(row.lang)] = intl.formatMessage({
                    id: 'ui-czna-authorities.vocabularies.form.translationRequired',
                });
            }
        });
        var primary = rows.find(function (r) { return r.lang === PRIMARY_LANGUAGE; });
        if (codeRequired && (!primary || !primary.code.trim())) {
            errors['code:cs'] = intl.formatMessage({
                id: 'ui-czna-authorities.vocabularies.form.codeRequired',
            });
        }
        setFieldErrors(errors);
        return Object.keys(errors).length === 0;
    }, [type, rows, codeRequired, intl]);
    var buildTranslations = useCallback(function () {
        var translations = {};
        rows.forEach(function (row) {
            var name = row.name.trim();
            var code = row.lang === PRIMARY_LANGUAGE && row.code.trim() ? row.code.trim() : undefined;
            translations[row.lang] = code ? { name: name, code: code } : { name: name };
        });
        return translations;
    }, [rows]);
    var handleSubmit = useCallback(function () { return __awaiter(void 0, void 0, void 0, function () {
        var translations, adopted, checkedKey;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    showError(null);
                    if (!validate())
                        return [2 /*return*/];
                    translations = buildTranslations();
                    if (isEdit && vocabulary) {
                        adopted = adoptedVersionRef.current;
                        updateMutation.mutate({
                            translations: translations,
                            version: adopted && adopted.from === vocabulary.version ? adopted.version : vocabulary.version,
                        });
                        return [2 /*return*/];
                    }
                    if (!!shownWarning) return [3 /*break*/, 2];
                    checkedKey = warningKey(type, primaryHeading);
                    return [4 /*yield*/, showsSimilarWarning()];
                case 1:
                    if (_a.sent())
                        return [2 /*return*/];
                    if (!mountedRef.current || currentKeyRef.current !== checkedKey)
                        return [2 /*return*/];
                    _a.label = 2;
                case 2:
                    createMutation.mutate({ type: type, translations: translations });
                    return [2 /*return*/];
            }
        });
    }); }, [isEdit, vocabulary, type, validate, buildTranslations, createMutation, updateMutation,
        shownWarning, showsSimilarWarning, primaryHeading, showError]);
    var typeOptions = useMemo(function () { return __spreadArray([
        { value: '', label: intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.form.selectType' }) }
    ], openTypeOptions, true); }, [intl, openTypeOptions]);
    var languageLabel = useCallback(function (lang) { return intl.formatMessage({
        id: "ui-czna-authorities.vocabularies.form.lang.".concat(lang),
    }); }, [intl]);
    var footer = (React.createElement(ModalFooter, null,
        React.createElement(Button, { buttonStyle: "primary", onClick: handleSubmit, disabled: isSubmitting, marginBottom0: true }, intl.formatMessage({
            id: isEdit
                ? 'ui-czna-authorities.vocabularies.form.save'
                : shownWarning
                    ? 'ui-czna-authorities.vocabularies.form.createAnyway'
                    : 'ui-czna-authorities.vocabularies.form.create',
        })),
        onAdvancedEdit && (React.createElement(Button, { buttonStyle: "default", onClick: function () { return onAdvancedEdit(vocabulary === null || vocabulary === void 0 ? void 0 : vocabulary.id, type); }, 
            // Create flow needs a type to build the pane URL — without it the
            // route would redirect straight back to the list. Edit flow always
            // has a type from the loaded record.
            disabled: isSubmitting || (!isEdit && !type), marginBottom0: true, "data-test-vocabulary-advanced-edit": true }, intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.form.advancedEdit' }))),
        React.createElement(Button, { buttonStyle: "default", onClick: onClose, marginBottom0: true }, intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.form.cancel' }))));
    return (React.createElement(Modal, { open: true, label: isEdit
            ? intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.form.edit' })
            : intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.form.create' }), footer: footer, onClose: onClose, dismissible: true, size: "medium" },
        errorMessage && (React.createElement(MessageBanner, { type: "error", dismissible: true, onExited: function () { return showError(null); } }, typeof errorMessage === 'string' ? errorMessage : (React.createElement(SaveOutcomeUnknownNotice, { key: failureSeq, failure: errorMessage, runVerify: runVerify })))),
        shownWarning && (React.createElement(MessageBanner, { type: "warning" },
            React.createElement("div", { "data-test-similar-spellings": true },
                React.createElement("p", null, intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.form.similar.intro' })),
                React.createElement("ul", null,
                    shownWarning.terms.map(function (term) { return (React.createElement("li", { key: term.id, "data-test-similar-term": true }, intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.form.similar.term' }, { heading: term.heading }))); }),
                    shownWarning.dataValues.map(function (dv) { return (React.createElement("li", { key: "".concat(dv.fieldTag, "$").concat(dv.subfieldCode, ":").concat(dv.value), "data-test-similar-data-value": true }, intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.form.similar.dataValue' }, {
                        value: dv.value,
                        field: dv.fieldTag,
                        subfield: dv.subfieldCode,
                        count: dv.occurrences,
                    }))); })),
                React.createElement("p", null, intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.form.similar.confirm' }))))),
        React.createElement(Select, { label: intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.form.type' }), dataOptions: typeOptions, value: type, onChange: function (e) { return setType(e.target.value); }, error: fieldErrors.type, required: true, disabled: isEdit || (lockType && Boolean(defaultType)) }),
        rows.map(function (row) {
            var isPrimary = row.lang === PRIMARY_LANGUAGE;
            return (React.createElement(Row, { key: row.lang, "data-test-translation-row": row.lang },
                React.createElement(Col, { xs: isPrimary ? 4 : 4 },
                    React.createElement(TextField, { label: intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.form.language' }), value: languageLabel(row.lang), readOnly: true, disabled: true })),
                React.createElement(Col, { xs: isPrimary ? 4 : 7 },
                    React.createElement(TextField, { label: intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.form.name' }), value: row.name, onChange: function (e) {
                            return handleRowChange(row.lang, { name: e.target.value });
                        }, error: fieldErrors["name:".concat(row.lang)], required: true, "data-test-translation-name": row.lang })),
                isPrimary && (React.createElement(Col, { xs: 4 },
                    React.createElement(TextField, { label: intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.form.code' }), value: row.code, onChange: function (e) {
                            return handleRowChange(row.lang, { code: e.target.value });
                        }, error: fieldErrors['code:cs'], required: codeRequired, "data-test-translation-code": "cs" }))),
                !isPrimary && (React.createElement(Col, { xs: 1, style: { display: 'flex', alignItems: 'flex-end' } },
                    React.createElement(IconButton, { icon: "trash", onClick: function () { return handleRemoveRow(row.lang); }, "aria-label": intl.formatMessage({
                            id: 'ui-czna-authorities.vocabularies.form.removeTranslation',
                        }), "data-test-remove-translation": row.lang })))));
        }),
        canAddTranslation && (React.createElement(Button, { buttonStyle: "link", onClick: handleAddTranslation, "data-test-add-translation": true, marginBottom0: true }, intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.form.addTranslation' }))),
        codeRequired && (React.createElement(MessageBanner, { type: "default" }, intl.formatMessage({ id: 'ui-czna-authorities.vocabularies.form.codeRequired' })))));
};
export default VocabularyForm;
